<template>
  <div>
    <NavBar/>
    <div class="attraction-table-container" :dir="isHebrew ? 'rtl' : 'ltr'">
      <div class="button-container">
        <button class="manage-categories-button" @click="navigateToCategories">{{ t('rideManagerAttractionManageCategories') }}</button>
        <button
          v-if="userId === 1"
          class="manage-categories-button"
          :disabled="enriching"
          @click="startEnrichAll"
        >
          {{ enriching ? t('enrichAllRunning') : t('enrichAllButton') }}
        </button>
      </div>

      <div v-if="userId === 1 && (enriching || enrichSummary || enrichError)" class="enrich-all-status">
        <p v-if="enrichProgress" class="enrich-all-progress">
          {{ t('enrichAllProgress', enrichProgress) }}
        </p>
        <p v-else-if="enriching" class="enrich-all-progress">{{ t('enrichAllStarting') }}</p>
        <p v-if="enriching" class="enrich-all-hint">{{ t('enrichAllHint') }}</p>
        <p v-if="enrichSummary" class="enrich-all-done">{{ enrichSummary }}</p>
        <p v-if="enrichError" class="enrich-all-error">{{ enrichError }}</p>
      </div>
      
      <!-- Tabs -->
      <div class="tabs-container">
        <button 
          class="tab-button" 
          :class="{ active: activeTab === 'attractions' }"
          @click="activeTab = 'attractions'">
          {{ t('rideManagerAttractionTabAttractions') }}
        </button>
        <button 
          class="tab-button" 
          :class="{ active: activeTab === 'accommodations' }"
          @click="activeTab = 'accommodations'">
          {{ t('rideManagerAttractionTabAccommodations') }}
        </button>
      </div>

      <!-- Attractions Table -->
      <table v-if="activeTab === 'attractions'" class="attraction-table">
        <thead>
          <tr>
            <th>{{ t('rideManagerAttractionName') }}</th>
            <th>{{ t('rideManagerAttractionDescription') }}</th>
            <th>{{ t('rideManagerAttractionHasImage') }}</th>
            <th>{{ t('rideManagerAttractionEdit') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in rideManagerAttractions" :key="index">
            <td>{{ item.title }}</td>
            <td>{{ truncateText(item.text) }}</td>
            <td>{{ item.image ? '✅' : '❌' }}</td>
            <td>
              <button class="edit-button" @click="cleanHistoryAndEditAttraction(item.id)">{{ t('rideManagerAttractionEditButton') }}</button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Accommodations Table -->
      <table v-if="activeTab === 'accommodations'" class="attraction-table">
        <thead>
          <tr>
            <th>{{ t('rideManagerAttractionName') }}</th>
            <th>{{ t('rideManagerAttractionDescription') }}</th>
            <th>{{ t('rideManagerAttractionHasImage') }}</th>
            <th>{{ t('rideManagerAttractionEdit') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in rideManagerAccommodations" :key="index">
            <td>{{ item.name || item.title }}</td>
            <td>{{ truncateText(item.text || item.address || '') }}</td>
            <td>{{ item.image ? '✅' : '❌' }}</td>
            <td>
              <button class="edit-button" @click="cleanHistoryAndEditAccommodation(item)">{{ t('rideManagerAttractionEditButton') }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <AppFooter/>
  </div>
</template>

<script>
import NavBar from "@/components/NavBar.vue";
import AppFooter from "@/components/Template/AppFooter.vue";
import { mapActions, mapGetters } from "vuex";
import attractionApi from "@/api/attraction";
import { pollJobStatus } from "@/common/pollJobStatus";

// One AI call per attraction, so a full pool can take well over an hour.
const ENRICH_ALL_TIMEOUT_MS = 2 * 60 * 60 * 1000;
import { translationMixin } from '@/mixins/translationMixin';

export default {
  name: 'RideManagerAttractions',
  mixins: [translationMixin],
  components: {
    AppFooter,
    NavBar,
  },
  data() {
    return {
      activeTab: 'attractions',
      enriching: false,
      enrichProgress: null,
      enrichSummary: '',
      enrichError: '',
    };
  },
  computed: {
    ...mapGetters([
      "rideManagerAttractions",
      "rideManagerAccommodations",
      "apptoken",
      "userId",
    ]),
  },

  beforeDestroy() {
    this._destroyed = true;
  },

  methods: {
    async startEnrichAll() {
      if (this.enriching) return;
      const count = (this.rideManagerAttractions || []).length;
      if (!window.confirm(this.t('enrichAllConfirm', { count }))) return;

      this.enriching = true;
      this.enrichProgress = null;
      this.enrichSummary = '';
      this.enrichError = '';

      try {
        const { data } = await attractionApi.enrichManagerAttractions(this.apptoken);
        if (!data || !data.success) {
          this.enrichError = data && data.error === 'user_is_not_admin'
            ? this.t('enrichmentNotAdmin')
            : this.t('enrichAllError');
          return;
        }

        const { status, result } = await pollJobStatus(
          () => attractionApi.getJobStatus(this.apptoken, data.job_id),
          { timeout: ENRICH_ALL_TIMEOUT_MS, onPending: this.showProgress },
        );
        if (this._destroyed) return;

        if (status !== 'complete' || !result || !result.success) {
          this.enrichError = this.t('enrichAllError');
          return;
        }

        this.enrichSummary = this.t('enrichAllDone', result);
        this.enrichProgress = null;
        await this.fetchRideMangerAttractions();
      } catch (e) {
        this.enrichError = this.t('enrichAllError');
      } finally {
        this.enriching = false;
      }
    },

    showProgress(progress) {
      if (!this._destroyed && progress) this.enrichProgress = progress;
    },

    ...mapActions([
      "cleanHistoryAndEditAttraction",
      "cleanHistoryAndEditAccommodation",
      "validateAndupdateAppToken",
      "fetchRideMangerAttractions",
      "fetchRideMangerAccommodations",
    ]),
    truncateText(text) {
      if (!text) return '';
      return text.length > 60 ? text.slice(0, 60) + '...' : text;
    },
    navigateToCategories() {
      this.$router.push('/admin/categories');
    },
  },
  created() {
    this.validateAndupdateAppToken();
    this.fetchRideMangerAttractions();
    this.fetchRideMangerAccommodations();
  },

}
</script>

<style scoped>
.enrich-all-status {
  margin: 8px 0 16px;
  font-size: 0.9em;
}

.enrich-all-progress {
  color: #2c7be5;
  margin: 0;
}

.enrich-all-hint {
  color: #888;
  font-size: 0.9em;
  margin: 2px 0 0;
}

.enrich-all-done {
  color: #1e7e34;
  margin: 4px 0 0;
}

.enrich-all-error {
  color: #c0392b;
  margin: 4px 0 0;
}
</style>

<style scoped>
.attraction-table-container {
  padding: 80px 30px;
  background: linear-gradient(45deg, #E0BE36, #DD6E42);
  border-radius: 10px;
  margin: 40px auto;
  max-width: 1000px;
  box-sizing: border-box;
}

.attraction-table-container[dir="ltr"] {
  direction: ltr;
}
.attraction-table-container[dir="rtl"] {
  direction: rtl;
}

/* Table layout */
.attraction-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  table-layout: auto;
}

.attraction-table-container[dir="rtl"] .attraction-table {
  direction: rtl;
}
.attraction-table-container[dir="ltr"] .attraction-table {
  direction: ltr;
}

/* Table headers and cells */
.attraction-table th,
.attraction-table td {
  padding: 12px 15px;
  border-bottom: 1px solid #ddd;
  font-size: 16px;
  word-wrap: break-word;
  white-space: normal;
}

.attraction-table-container[dir="rtl"] .attraction-table th,
.attraction-table-container[dir="rtl"] .attraction-table td {
  text-align: right;
}
.attraction-table-container[dir="ltr"] .attraction-table th,
.attraction-table-container[dir="ltr"] .attraction-table td {
  text-align: left;
}

/* Header styling */
.attraction-table th {
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: white;
  white-space: nowrap;
}

/* Edit button */
.edit-button {
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
}

.edit-button:hover {
  opacity: 0.85;
}

.button-container {
  margin-bottom: 20px;
}

.attraction-table-container[dir="rtl"] .button-container {
  text-align: left;
}
.attraction-table-container[dir="ltr"] .button-container {
  text-align: left;
}

.manage-categories-button {
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  transition: opacity 0.2s;
}

.manage-categories-button:hover {
  opacity: 0.85;
}

/* Tabs styling */
.tabs-container {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.attraction-table-container[dir="rtl"] .tabs-container {
  direction: rtl;
}
.attraction-table-container[dir="ltr"] .tabs-container {
  direction: ltr;
}

.tab-button {
  background: white;
  color: #333;
  border: 2px solid #ddd;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  transition: all 0.2s;
  flex: 1;
}

.tab-button:hover {
  border-color: #34D0B6;
  background: #f0f0f0;
}

.tab-button.active {
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: white;
  border-color: transparent;
}

/* 🌟 Mobile responsiveness */
@media (max-width: 600px) {
  .attraction-table-container {
    padding: 20px 10px;
    padding-top: 80px; /* clear fixed navbar on mobile (both RTL and LTR) */
    margin: 20px 10px;
    overflow-x: hidden; /* prevent horizontal page scroll when table fits */
    min-width: 0;       /* allow flex/grid children to shrink below content size */
  }

  .edit-button {
    padding: 5px 10px;
    font-size: 13px;
  }

  .attraction-table th,
  .attraction-table td {
    padding: 8px 10px;
    font-size: 14px;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  .attraction-table {
    font-size: 13px;
    table-layout: fixed; /* keep table within container width (fixes RTL horizontal scroll) */
    width: 100%;
  }

  /* allow headers to wrap on mobile so table doesn't overflow */
  .attraction-table th {
    white-space: normal;
  }

  .button-container {
    margin-bottom: 15px;
  }
  
  .manage-categories-button {
    padding: 8px 16px;
    font-size: 14px;
  }

  .tabs-container {
    flex-direction: column;
    gap: 8px;
  }

  .tab-button {
    padding: 8px 16px;
    font-size: 14px;
  }
}
</style>
